/**
 * BRAISE : un charbon qui couve. Deux chaleurs y respirent a contretemps, et une etincelle monte
 * de loin en loin, hors du disque.
 *
 * L'IDEE, EN UN MOT : hospitalite, hiver. Une braise n'appelle pas, elle attire ; c'est le seul
 * look du catalogue dont la promesse est la chaleur et non la disponibilite.
 *
 * L'EXCEPTION DE COULEUR EST ASSUMEE, ET C'EST LA REGLE DE L'OWNER : une braise est ambree, sinon
 * ce n'est plus une braise. La couleur du client teinte LE CHARBON, jamais le foyer. Le repli des
 * navigateurs sans color-mix garde donc le meme feu, sur un charbon qui n'est plus teinte : une
 * braise moins personnelle, pas une braise eteinte.
 *
 * AU TEMPS FORT, une gerbe d'etincelles jaillit. Les etincelles montent de quarante-huit pixels
 * au-dessus du centre, donc HORS du disque : elles vivent dans leur propre couche, a cote du
 * disque qui, lui, rogne ses chaleurs.
 *
 * CHAQUE ETINCELLE PORTE SON PROPRE DEPART, en variables posees au montage, et ce retard est
 * ECRIT DANS LE RACCOURCI animation, pas dans une regle a part : une regle a part pese moins que
 * celle qui porte le raccourci, et son retard serait remis a zero sans que rien ne le dise.
 *
 * A L'OUVERTURE la braise se couvre et les etincelles cessent ; une croix de fer rouge s'allume
 * a sa surface — la croix commune du modele, mais chauffee.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */
export default {
  id: 'braise',

  css: `
/* LE CHARBON. Le foyer est au tiers bas du disque, la ou l'air passe le moins : c'est de la que
   monte la chaleur. Le repli garde le meme feu sur un charbon non teinte. */
.woot-lanceur[data-look="braise"] .woot-braise__disque {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  overflow: hidden;
  background: radial-gradient(circle at 50% 76%, #ffc26b 0%, #e2581a 26%, #2a1008 62%, #140806 100%);
  background: radial-gradient(circle at 50% 76%, #ffc26b 0%, #e2581a 26%, color-mix(in srgb, var(--client) 30%, #2a1008) 62%, #140806 100%);
  box-shadow: 0 14px 36px rgba(226, 88, 26, .45), 0 0 0 1px rgba(255, 170, 90, .18);
  transition: filter .35s ease;
}

/* LES DEUX CHALEURS RESPIRENT A CONTRETEMPS : deux sept et trois neuf secondes, l'une a l'envers.
   Deux durees premieres entre elles, donc une boucle commune qu'on ne peut pas compter du regard.
   Chacune a sa propre regle d'etat plutot qu'un reglage pose a cote : a specificite egale, c'est
   la derniere declaree qui gagne, et la seconde chaleur garde vraiment sa duree. */
.woot-lanceur[data-look="braise"] .woot-braise__chaleur {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 45% 66%, rgba(255, 192, 122, .75), transparent 55%);
}

.woot-lanceur[data-look="braise"] .woot-braise__chaleur--deux {
  background: radial-gradient(circle at 60% 56%, rgba(255, 140, 80, .55), transparent 50%);
}

.woot-lanceur[data-look="braise"][data-etat="repos"] .woot-braise__chaleur,
.woot-lanceur[data-look="braise"][data-etat="attention"] .woot-braise__chaleur {
  animation: woot-braise-souffle 2.7s ease-in-out infinite;
}

.woot-lanceur[data-look="braise"][data-etat="repos"] .woot-braise__chaleur--deux,
.woot-lanceur[data-look="braise"][data-etat="attention"] .woot-braise__chaleur--deux {
  animation: woot-braise-souffle 3.9s ease-in-out infinite reverse;
}

@keyframes woot-braise-souffle {
  0%, 100% { opacity: .5; transform: scale(1); }
  50% { opacity: .95; transform: scale(1.06); }
}

/* LES ETINCELLES VIVENT HORS DU DISQUE, qui rogne. Chacune part du foyer et derive de son propre
   ecart lateral ; au repos elle ne devient visible qu'a la toute fin de sa boucle de sept
   secondes, donc on en voit une de temps en temps, jamais quatre ensemble.

   L'ETINCELLE EST ORANGEE, PAS CREME, ET ELLE GARDE SON EPAISSEUR PRESQUE JUSQU'EN HAUT. Un point
   creme entoure d'un halo chaud disparait sur une page blanche : mesure faite sur la capture, il
   n'y avait plus rien a voir. Le filet sombre d'un pixel lui donne un bord meme sur du blanc. */
.woot-lanceur[data-look="braise"] .woot-braise__etincelles { position: absolute; inset: 0; }

.woot-lanceur[data-look="braise"] .woot-braise__etincelle {
  position: absolute;
  left: 50%;
  bottom: 50%;
  width: 3.5px;
  height: 3.5px;
  border-radius: 50%;
  background: #ff9d45;
  box-shadow: 0 0 7px 2px rgba(255, 150, 65, .8), 0 0 0 1px rgba(120, 45, 10, .3);
  opacity: 0;
}

.woot-lanceur[data-look="braise"][data-etat="repos"] .woot-braise__etincelle {
  animation: woot-braise-monte 7s ease-out var(--woot-braise-retard) infinite;
}

@keyframes woot-braise-monte {
  0%, 80% { opacity: 0; transform: translate(0, 0); }
  83%, 94% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--woot-braise-ecart), -48px); }
}

/* LE TEMPS FORT : la gerbe. Les quatre partent presque ensemble, avec un elan propre a chacune —
   sans ce decalage elles montent en ligne, et quatre points alignes ne se lisent plus comme des
   etincelles mais comme trois petits points qui attendent. Vu sur la capture, corrige. */
.woot-lanceur[data-look="braise"][data-etat="attention"] .woot-braise__etincelle {
  animation: woot-braise-jaillit 1.5s ease-out var(--woot-braise-elan);
}

@keyframes woot-braise-jaillit {
  0% { opacity: 0; transform: translate(0, 0); }
  12%, 72% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--woot-braise-ecart), -56px); }
}

/* AU SURVOL on souffle dessus : elle s'avive. */
.woot-lanceur[data-look="braise"]:hover .woot-braise__disque { filter: brightness(1.15) saturate(1.15); }

/* OUVERT : la braise se couvre, les etincelles cessent, et la croix s'allume au fer rouge. Sa
   couleur est un ambre en dur, donc le jeton personnalise du modele suffit ici : ce qui n'a pas
   de chaine de repli dans une propriete personnalisee, c'est un color-mix, et il n'y en a pas. */
.woot-lanceur[data-look="braise"] .woot-lanceur__croix {
  --woot-croix: #ffd28a;
  width: 22px;
  height: 22px;
  margin: -11px;
}

.woot-lanceur[data-look="braise"] .woot-lanceur__croix::before,
.woot-lanceur[data-look="braise"] .woot-lanceur__croix::after {
  height: 2.4px;
  box-shadow: 0 0 10px 3px rgba(255, 150, 60, .7), 0 0 2px #ffffff;
}

.woot-lanceur[data-look="braise"][data-etat="ouvert"] .woot-braise__disque { filter: brightness(.78) saturate(.9); }
.woot-lanceur[data-look="braise"][data-etat="ouvert"] .woot-braise__etincelles { visibility: hidden; }
`,

  monter: conteneur => {
    const disque = document.createElement('div');
    disque.className = 'woot-braise__disque';
    ['', ' woot-braise__chaleur--deux'].forEach(rang => {
      const chaleur = document.createElement('span');
      chaleur.className = 'woot-braise__chaleur' + rang;
      disque.appendChild(chaleur);
    });

    // Ecart lateral, retard au repos, elan au temps fort : trois valeurs par etincelle, ecrites a
    // la main. Une suite tiree au hasard changerait de page en page, et une braise qui n'a pas deux
    // fois le meme feu n'est plus la meme marque.
    const etincelles = document.createElement('div');
    etincelles.className = 'woot-braise__etincelles';
    [
      ['-14px', '0s', '0s'],
      ['9px', '2.3s', '.17s'],
      ['-4px', '4.1s', '.07s'],
      ['16px', '5.6s', '.24s'],
    ].forEach(([ecart, retard, elan]) => {
      const etincelle = document.createElement('i');
      etincelle.className = 'woot-braise__etincelle';
      etincelle.style.setProperty('--woot-braise-ecart', ecart);
      etincelle.style.setProperty('--woot-braise-retard', retard);
      etincelle.style.setProperty('--woot-braise-elan', elan);
      etincelles.appendChild(etincelle);
    });

    const croix = document.createElement('span');
    croix.className = 'woot-lanceur__croix';

    conteneur.appendChild(disque);
    conteneur.appendChild(etincelles);
    conteneur.appendChild(croix);
  },

  // BRAISE EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous
  // les looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et il n'y a rien a mettre en pause que la feuille n'arrete deja.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
